// IGDB API service via Cloudflare Worker relay
// Replaces MobyGames for fetching game cover images and screenshots

const IGDB_RELAY_URL = 'https://igdb.rgm.games';
const IGDB_API_KEY = 'f939740dd0ea72a63fe32443ab1ae60b3bb941751acb1433cab0ac3fbf750709';

// Rate limiting: IGDB allows 4 requests per second
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

/**
 * Sleep for specified milliseconds
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Make a POST request to the IGDB relay with retry logic
 */
async function igdbFetch(endpoint, body, retries = MAX_RETRIES) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${IGDB_RELAY_URL}/v4/${endpoint}`, {
        method: 'POST',
        headers: {
          'X-API-Key': IGDB_API_KEY,
          'Content-Type': 'text/plain',
        },
        body,
      });

      if (response.status === 429) {
        if (attempt < retries) {
          const delay = BASE_DELAY_MS * Math.pow(2, attempt);
          console.log(`IGDB rate limited (429). Retrying in ${delay}ms... (attempt ${attempt + 1}/${retries})`);
          await sleep(delay);
          continue;
        }
      }

      if (!response.ok) {
        console.error(`IGDB API error: ${response.status} ${response.statusText}`);
        return [];
      }

      return await response.json();
    } catch (error) {
      if (attempt < retries) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        console.warn(`IGDB fetch error, retrying in ${delay}ms...`, error);
        await sleep(delay);
        continue;
      }
      console.error('IGDB fetch failed after retries:', error);
      return [];
    }
  }
  return [];
}

/**
 * Build an IGDB image URL from an image_id
 * Sizes: thumb, cover_small, cover_big, screenshot_med, screenshot_big, screenshot_huge, 720p, 1080p
 */
function igdbImageUrl(imageId, size = 'cover_big') {
  // Use Nginx reverse proxy to avoid COEP blocking (images.igdb.com lacks CORP headers)
  return `/igdb-img/igdb/image/upload/t_${size}/${imageId}.jpg`;
}

// Platform name to IGDB platform ID mapping
const PLATFORM_ID_MAP = {
  // DOS/PC
  'dos': 13,
  'windows': 6,
  'win95': 6,
  'win98': 6,
  'windows 3.x': 6,
  'pc': 6,

  // Nintendo
  'nes': 18,
  'fc': 18,
  'famicom': 18,
  'snes': 19,
  'sfc': 19,
  'super famicom': 19,
  'n64': 4,
  'nintendo 64': 4,
  'game boy': 33,
  'gb': 33,
  'game boy color': 22,
  'gbc': 22,
  'game boy advance': 24,
  'gba': 24,
  'nds': 20,
  'nintendo ds': 20,
  'ds': 20,
  'virtual boy': 87,

  // Sony
  'playstation': 7,
  'ps': 7,
  'ps1': 7,
  'psx': 7,
  'playstation 2': 8,
  'ps2': 8,
  'psp': 38,
  'playstation portable': 38,

  // Sega
  'genesis': 29,
  'mega drive': 29,
  'md': 29,
  'sega genesis': 29,
  'sega cd': 78,
  'saturn': 32,
  'sega saturn': 32,
  'dreamcast': 23,
  'game gear': 35,
  'master system': 64,
  'sms': 64,

  // Atari
  'atari 2600': 59,
  'atari': 59,
  'atari 7800': 60,
  'atari st': 63,
  'atari lynx': 61,

  // Other
  'arcade': 52,
  'apple ii': 75,
  'apple iigs': 115,
  'commodore 64': 15,
  'c64': 15,
  'amiga': 16,
  'msx': 27,
  'turbografx-16': 86,
  'pc engine': 86,
  'neo geo': 80,
  '3do': 50,
};

/**
 * Get IGDB platform ID from platform name
 */
export function getPlatformId(platformName) {
  const normalized = platformName.toLowerCase().trim();

  // Direct match
  if (PLATFORM_ID_MAP[normalized]) {
    return PLATFORM_ID_MAP[normalized];
  }

  // Partial match
  for (const [key, id] of Object.entries(PLATFORM_ID_MAP)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return id;
    }
  }

  return null;
}

/**
 * Search for games on IGDB by title, optionally filtered by platform.
 * Returns games with cover image_id and basic info.
 */
async function searchGames(title, platformId) {
  let query = `search "${title}"; fields name,cover.image_id,platforms.name,first_release_date,summary; limit 5;`;

  if (platformId) {
    query = `search "${title}"; fields name,cover.image_id,platforms.name,first_release_date,summary; where platforms = (${platformId}); limit 5;`;
  }

  console.log(`[IGDB searchGames] Query: title="${title}", platformId=${platformId ?? 'none'}`);
  console.log(`[IGDB searchGames] Raw query: ${query}`);
  const results = await igdbFetch('games', query);
  console.log(`[IGDB searchGames] Results (${results.length}):`, JSON.stringify(results, null, 2));
  return results;
}

/**
 * Get screenshots for a game by IGDB game ID
 */
async function getGameScreenshots(gameId, limit = 5) {
  const results = await igdbFetch(
    'screenshots',
    `fields image_id; where game = ${gameId}; limit ${limit};`
  );

  return results
    .filter((s) => s.image_id)
    .map((s) => igdbImageUrl(s.image_id, 'screenshot_big'));
}

/**
 * Fetch cover and screenshots for a game by title and platform.
 * This is the main function used by gemini.js — drop-in replacement for MobyGames fetchGameImages.
 */
export async function fetchGameImages(gameTitle, platformName) {
  try {
    const platformId = platformName ? getPlatformId(platformName) : null;
    console.log(`[IGDB fetchGameImages] Looking up: title="${gameTitle}", platform="${platformName}", resolvedPlatformId=${platformId}`);

    // Search with platform filter first, fall back to unfiltered
    let games = await searchGames(gameTitle, platformId || undefined);
    if (games.length === 0 && platformId) {
      console.log(`[IGDB fetchGameImages] No results with platform filter, retrying without platform...`);
      games = await searchGames(gameTitle);
    }

    if (games.length === 0) {
      console.log(`IGDB: No games found for: ${gameTitle}`);
      return { poster: null, screenshots: [], found: false };
    }

    // Prefer a result that has a cover image
    const game = games.find((g) => g.cover?.image_id) || games[0];

    // Build cover URL from cover.image_id
    let poster = null;
    if (game.cover?.image_id) {
      poster = igdbImageUrl(game.cover.image_id, 'cover_big');
    }

    // Fetch screenshots
    let screenshots = [];
    try {
      screenshots = await getGameScreenshots(game.id, 3);
    } catch (err) {
      console.warn(`IGDB: Failed to fetch screenshots for game ${game.id}:`, err);
    }

    return { poster, screenshots, found: true };
  } catch (error) {
    console.error('IGDB fetchGameImages error:', error);
    return { poster: null, screenshots: [], found: false };
  }
}
